import React, { useEffect, useMemo, useState } from 'react';
import {
  Check, Clock, Copy, Crown, Inbox, KeyRound, MessageSquare, Pencil, Plus, Search, ShieldCheck, Undo2, UserCheck, UserRound,
  Users, UserX,
} from 'lucide-react';
import { adminService, apiErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import { AppShell, Badge, Button, Card, EmptyState, Field, Loader, Modal, PageHeader, Tabs } from '../components/ui';
import './AdminUsers.css';

const ROLE_OPTIONS = [
  { v: 'USER', l: 'Usuário', d: 'Estuda os cursos liberados' },
  { v: 'ADMIN', l: 'Admin', d: 'Edita cursos, trilhas e artigos' },
  { v: 'SUPER_ADMIN', l: 'Super admin', d: 'Também gerencia pessoas e papéis' },
];

const isAdminRole = (role) => role === 'ADMIN' || role === 'SUPER_ADMIN';

function RoleBadge({ role }) {
  if (role === 'SUPER_ADMIN') return <Badge tone="solid" icon={Crown}>Super admin</Badge>;
  return <Badge tone={role === 'ADMIN' ? 'solid' : 'navy'}>{role === 'ADMIN' ? 'Admin' : 'Usuário'}</Badge>;
}

const EMPTY_FORM = { username: '', name: '', email: '', role: 'USER', allCourses: false, courseIds: [], active: true };

function formatDate(value) {
  if (!value) return 'Nunca';
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AdminUsers() {
  const { user: me } = useAuth();
  const { courses } = useCourse();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | user
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [secret, setSecret] = useState(null); // { name, username, password, created }
  const [confirmReset, setConfirmReset] = useState(null);
  const [tab, setTab] = useState('users');
  const [requests, setRequests] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);

  const load = async () => {
    try {
      const [list, reqs] = await Promise.all([adminService.listUsers(), adminService.listRequests()]);
      setUsers(list);
      setRequests(reqs);
      window.dispatchEvent(new CustomEvent('access-requests:changed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const courseName = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c.name])), [courses]);

  const filtered = users.filter(u => {
    const q = query.trim().toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || u.username.includes(q) || (u.email || '').toLowerCase().includes(q);
  });
  // Alvo do tour "Cursos liberados": o primeiro usuário da lista que não é admin
  const firstStudentId = filtered.find(u => !isAdminRole(u.role))?.id;

  const openNew = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditing('new');
  };

  const openEdit = (u) => {
    setForm({
      username: u.username, name: u.name, email: u.email || '', role: u.role,
      allCourses: u.allCourses, courseIds: u.courseIds, active: u.active,
    });
    setFormError(null);
    setEditing(u);
  };

  const toggleCourse = (id) => {
    setForm(f => ({
      ...f,
      courseIds: f.courseIds.includes(id) ? f.courseIds.filter(c => c !== id) : [...f.courseIds, id],
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (editing === 'new') {
        const res = await adminService.createUser(form);
        setSecret({ name: res.user.name, username: res.user.username, password: res.temporaryPassword, created: true });
      } else {
        await adminService.updateUser(editing.id, form);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    const u = confirmReset;
    setConfirmReset(null);
    try {
      const res = await adminService.resetPassword(u.id);
      setSecret({ name: u.name, username: u.username, password: res.temporaryPassword, created: false });
      load();
    } catch (err) {
      alertError(err);
    }
  };

  const [toast, setToast] = useState(null);
  const alertError = (err) => setToast(apiErrorMessage(err));

  if (loading) {
    return (
      <AppShell section="Usuários">
        <Loader />
      </AppShell>
    );
  }

  const isSelf = editing && editing !== 'new' && editing.id === me.id;
  // O único super admin ativo não pode trocar o próprio papel (ninguém mais gerenciaria pessoas)
  const soleSuper = isSelf && users.filter(u => u.role === 'SUPER_ADMIN' && u.active).length <= 1;
  const pending = requests.filter(r => r.status === 'pending');

  const reject = async () => {
    const r = confirmReject;
    setConfirmReject(null);
    try {
      await adminService.rejectRequest(r.id);
      load();
    } catch (err) {
      alertError(err);
    }
  };

  const undoReject = async (r) => {
    try {
      await adminService.undoReject(r.id);
      load();
    } catch (err) {
      alertError(err);
    }
  };

  return (
    <AppShell section="Usuários">
      <PageHeader
        icon={Users}
        eyebrow="Administração"
        title="Usuários"
        subtitle="Crie contas, defina o papel e escolha quais cursos cada pessoa pode estudar."
        actions={<Button variant="accent" icon={Plus} onClick={openNew}>Novo usuário</Button>}
      />

      <div data-tour="pendentes">
      <Tabs
        className={`au-tabs ${pending.length ? 'au-tabs--pending' : ''}`}
        tabs={[
          { value: 'users', label: 'Usuários', icon: Users, count: users.length },
          { value: 'requests', label: 'Solicitações', icon: Inbox, count: pending.length },
        ]}
        value={tab}
        onChange={setTab}
      />
      </div>

      {toast && (
        <div className="au-toast" role="alert">
          <span>{toast}</span>
          <button type="button" className="ss-iconbtn" onClick={() => setToast(null)} aria-label="Fechar">×</button>
        </div>
      )}

      {tab === 'requests' ? (
        <RequestList
          requests={requests}
          onReview={setReviewing}
          onReject={setConfirmReject}
          onUndo={undoReject}
        />
      ) : (
      <>
      <div className="au-toolbar">
        <label className="au-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            className="ss-input"
            placeholder="Buscar por nome, login ou e-mail"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Buscar usuários"
          />
        </label>
        <span className="au-count">{filtered.length} {filtered.length === 1 ? 'usuário' : 'usuários'}</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState file="usuarios.xml" title="Nenhum usuário encontrado" description="Ajuste a busca ou crie um novo usuário." />
      ) : (
        <div className="au-list" data-tour="usuarios-lista">
          {filtered.map((u, i) => (
            <Card key={u.id} padding="md" className={`au-row ${u.active ? '' : 'au-row--inactive'}`}>
              <span className={`au-avatar ${isAdminRole(u.role) ? 'au-avatar--admin' : ''}`} aria-hidden="true">
                {u.role === 'SUPER_ADMIN' ? <Crown size={20} /> : u.role === 'ADMIN' ? <ShieldCheck size={20} /> : <UserRound size={20} />}
              </span>
              <div className="au-row__who">
                <span className="au-row__name">
                  {u.name}
                  {u.id === me.id && <span className="au-row__you">você</span>}
                </span>
                <span className="au-row__login">{u.username}{u.email ? ` · ${u.email}` : ''}</span>
              </div>
              <div className="au-row__access">
                <span className="au-row__role" data-tour={i === 0 ? 'usuario-papel' : undefined}><RoleBadge role={u.role} /></span>
                {!isAdminRole(u.role) && (
                  <span className="au-row__courses" data-tour={u.id === firstStudentId ? 'usuario-cursos' : undefined}>
                    {u.allCourses
                      ? <Badge tone="lime">Todos os cursos</Badge>
                      : u.courseIds.length === 0
                        ? <Badge tone="lime">Só artigos</Badge>
                        : u.courseIds.map(id => <Badge key={id} tone="neutral">{courseName[id] || `Curso ${id}`}</Badge>)}
                  </span>
                )}
                {!u.active && <Badge tone="danger">Desativado</Badge>}
                {u.mustChangePassword && u.active && <Badge tone="warning">Senha temporária</Badge>}
              </div>
              <div className="au-row__meta">
                <span>Último acesso</span>
                <strong>{formatDate(u.lastLoginAt)}</strong>
              </div>
              <div className="au-row__actions">
                <Button size="sm" variant="outline" icon={Pencil} onClick={() => openEdit(u)}>Editar</Button>
                <Button size="sm" variant="ghost" icon={KeyRound} onClick={() => setConfirmReset(u)}>Redefinir senha</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      </>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Novo usuário' : 'Editar usuário'}
        width={560}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name.trim() || !form.username.trim()}>
              {saving ? 'Salvando...' : editing === 'new' ? 'Criar usuário' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form onSubmit={save} className="au-form">
          {formError && <div className="au-form__error" role="alert">{formError}</div>}
          <div className="au-form__two">
            <Field label="Nome" required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </Field>
            <Field label="Login" required hint={editing === 'new' ? 'Minúsculas, números, ponto ou hífen' : 'O login não pode ser alterado'}>
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                disabled={editing !== 'new'}
                autoCapitalize="none"
                spellCheck={false}
              />
            </Field>
          </div>
          <Field label="E-mail" hint="Opcional, só para identificação">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>

          <fieldset className="au-fieldset">
            <legend>Papel</legend>
            <div className="au-segment">
              {ROLE_OPTIONS.map(o => (
                <button
                  key={o.v}
                  type="button"
                  className={form.role === o.v ? 'is-on' : ''}
                  aria-pressed={form.role === o.v}
                  onClick={() => setForm({ ...form, role: o.v })}
                  disabled={soleSuper && o.v !== 'SUPER_ADMIN'}
                >
                  <strong>{o.l}</strong>
                  <span>{o.d}</span>
                </button>
              ))}
            </div>
          </fieldset>

          {form.role === 'USER' && (
            <fieldset className="au-fieldset">
              <legend>Cursos liberados</legend>
              <label className="au-check au-check--all">
                <input type="checkbox" checked={form.allCourses} onChange={(e) => setForm({ ...form, allCourses: e.target.checked })} />
                <span>
                  <strong>Todos os cursos</strong>
                  <small>Inclui cursos criados no futuro</small>
                </span>
              </label>
              {!form.allCourses && (
                <div className="au-courses">
                  {courses.map(c => (
                    <label key={c.id} className="au-check">
                      <input type="checkbox" checked={form.courseIds.includes(c.id)} onChange={() => toggleCourse(c.id)} />
                      <span><strong>{c.name}</strong></span>
                    </label>
                  ))}
                </div>
              )}
              {!form.allCourses && form.courseIds.length === 0 && (
                <p className="au-form__hint">Sem cursos marcados, a conta fica só com os artigos.</p>
              )}
            </fieldset>
          )}

          {editing !== 'new' && (
            <label className="au-check">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                disabled={isSelf}
              />
              <span>
                <strong>Conta ativa</strong>
                <small>Desativar encerra as sessões abertas e bloqueia o login</small>
              </span>
            </label>
          )}
          {editing === 'new' && (
            <p className="au-form__hint">Uma senha temporária será gerada. A pessoa troca no primeiro acesso.</p>
          )}
        </form>
      </Modal>

      <Modal
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        title="Redefinir senha?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmReset(null)}>Cancelar</Button>
            <Button icon={KeyRound} onClick={resetPassword}>Gerar senha temporária</Button>
          </>
        }
      >
        <p>
          A senha atual de <strong>{confirmReset?.name}</strong> deixa de funcionar e as sessões abertas são encerradas.
          Uma senha temporária será gerada para você repassar.
        </p>
      </Modal>

      {reviewing && (
        <ApproveModal
          request={reviewing}
          courses={courses}
          onClose={() => setReviewing(null)}
          onApproved={(res) => {
            setReviewing(null);
            setSecret({ name: res.user.name, username: res.user.username, password: res.temporaryPassword, created: true });
            load();
          }}
        />
      )}

      <Modal
        open={!!confirmReject}
        onClose={() => setConfirmReject(null)}
        title="Recusar solicitação?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmReject(null)}>Cancelar</Button>
            <Button variant="danger" icon={UserX} onClick={reject}>Recusar</Button>
          </>
        }
      >
        <p>
          O pedido de <strong>{confirmReject?.name}</strong> ({confirmReject?.username}) sai da lista de pendentes.
          Dá para desfazer por 7 dias.
        </p>
      </Modal>

      <SecretModal secret={secret} onClose={() => setSecret(null)} />
    </AppShell>
  );
}

/** Solicitações pendentes e as recusadas há menos de 7 dias (com "Desfazer"). */
function RequestList({ requests, onReview, onReject, onUndo }) {
  if (requests.length === 0) {
    return (
      <EmptyState
        file="solicitacoes.xml"
        title="Nenhuma solicitação"
        description="Quem não tem conta pode pedir acesso pela tela de login. Os pedidos aparecem aqui."
      />
    );
  }
  return (
    <>
      <div className="au-list">
        {requests.map(r => {
          const rejected = r.status === 'rejected';
          return (
            <Card key={r.id} padding="md" className={`au-row au-row--request ${rejected ? 'au-row--inactive' : ''}`}>
              <span className="au-avatar au-avatar--pending" aria-hidden="true"><Clock size={20} /></span>
              <div className="au-row__who">
                <span className="au-row__name">{r.name}</span>
                <span className="au-row__login">{r.username} · {r.email}</span>
              </div>
              <div className="au-row__access au-row__access--request">
                <span className="au-row__badges">
                  {r.courses.map(c => <Badge key={c.id} tone="neutral">{c.name}</Badge>)}
                  {(r.trails || []).map(t => <Badge key={`t${t.id}`} tone="navy">Trilha · {t.shortTitle || t.title}</Badge>)}
                  {r.articlesOnly && <Badge tone="lime">Só artigos</Badge>}
                </span>
                {r.message && (
                  <span className="au-row__message" title={r.message}>
                    <MessageSquare size={13} aria-hidden="true" />“{r.message}”
                  </span>
                )}
              </div>
              <div className="au-row__meta">
                <span>{rejected ? 'Recusada' : 'Solicitado'}</span>
                <strong>{formatDate(rejected ? r.reviewedAt : r.createdAt)}</strong>
              </div>
              <div className="au-row__actions">
                {rejected ? (
                  <Button size="sm" variant="ghost" icon={Undo2} onClick={() => onUndo(r)}>Desfazer</Button>
                ) : (
                  <>
                    <Button size="sm" variant="accent" icon={UserCheck} onClick={() => onReview(r)}>Revisar e aprovar</Button>
                    <Button size="sm" variant="ghost" icon={UserX} onClick={() => onReject(r)}>Recusar</Button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      <p className="au-form__hint au-requests__note">Solicitações recusadas somem da lista depois de 7 dias.</p>
    </>
  );
}

/** "Finalizar cadastro": dados do pedido, editáveis, e os cursos já marcados com o que foi pedido. */
function ApproveModal({ request, courses, onClose, onApproved }) {
  const [form, setForm] = useState({
    name: request.name,
    username: request.username,
    email: request.email,
    role: 'USER',
    allCourses: false,
    courseIds: request.suggestedCourseIds || request.courseIds,
    active: true,
  });
  // Curso marcado por causa de uma trilha pedida: mostra qual
  const viaTrail = (courseId) => (request.trails || []).find(t => t.courseIds.includes(courseId));
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const u = form.username.trim();
    if (u.length < 3) { setAvailable(null); return undefined; }
    const t = setTimeout(() => {
      adminService.usernameAvailable(u, request.id).then(setAvailable).catch(() => setAvailable(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.username, request.id]);

  const toggleCourse = (id) => setForm(f => ({
    ...f,
    courseIds: f.courseIds.includes(id) ? f.courseIds.filter(c => c !== id) : [...f.courseIds, id],
  }));

  const approve = async () => {
    setSaving(true);
    setError(null);
    try {
      onApproved(await adminService.approveRequest(request.id, form));
    } catch (err) {
      setError(apiErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Finalizar cadastro"
      width={560}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button icon={UserCheck} onClick={approve} disabled={saving || !form.name.trim() || available === false}>
            {saving ? 'Criando...' : 'Aprovar e criar conta'}
          </Button>
        </>
      }
    >
      <div className="au-form">
        {error && <div className="au-form__error" role="alert">{error}</div>}
        {request.message && (
          <div className="au-request-msg">
            <MessageSquare size={15} aria-hidden="true" />
            <span><strong>Mensagem do solicitante:</strong> {request.message}</span>
          </div>
        )}
        <div className="au-form__two">
          <Field label="Nome" required>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field
            label="Login"
            required
            hint={available === null ? 'Minúsculas, números, ponto ou hífen' : available ? 'Disponível ✓' : 'Já está em uso'}
          >
            <input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={available === false}
            />
          </Field>
        </div>
        <Field label="E-mail" hint="O sistema ainda não envia e-mail: repasse o login e a senha temporária.">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <fieldset className="au-fieldset">
          <legend>Papel</legend>
          <div className="au-segment">
            {ROLE_OPTIONS.map(o => (
              <button key={o.v} type="button" className={form.role === o.v ? 'is-on' : ''} aria-pressed={form.role === o.v}
                      onClick={() => setForm({ ...form, role: o.v })}>
                <strong>{o.l}</strong>
                <span>{o.d}</span>
              </button>
            ))}
          </div>
        </fieldset>
        {form.role === 'USER' && (
          <fieldset className="au-fieldset">
            <legend>Cursos liberados</legend>
            <label className="au-check au-check--all">
              <input type="checkbox" checked={form.allCourses} onChange={(e) => setForm({ ...form, allCourses: e.target.checked })} />
              <span><strong>Todos os cursos</strong><small>Inclui cursos criados no futuro</small></span>
            </label>
            {!form.allCourses && (
              <div className="au-courses">
                {courses.map(c => (
                  <label key={c.id} className="au-check">
                    <input type="checkbox" checked={form.courseIds.includes(c.id)} onChange={() => toggleCourse(c.id)} />
                    <span>
                      <strong>{c.name}</strong>
                      {request.courseIds.includes(c.id)
                        ? <small>Pedido pelo solicitante</small>
                        : viaTrail(c.id) && <small>Usado pela trilha {viaTrail(c.id).shortTitle || viaTrail(c.id).title}</small>}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {!form.allCourses && form.courseIds.length === 0 && (
              <p className="au-form__hint">
                {request.articlesOnly ? 'Pedido como "Só artigos": ' : ''}sem cursos, a conta fica só com os artigos.
              </p>
            )}
          </fieldset>
        )}
        <p className="au-form__hint">Uma senha temporária será gerada. A pessoa troca no primeiro acesso.</p>
      </div>
    </Modal>
  );
}

/** Mostra a senha temporária uma única vez, com botão de copiar. */
function SecretModal({ secret, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [secret]);
  if (!secret) return null;
  const text = `Login: ${secret.username}\nSenha temporária: ${secret.password}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={secret.created ? 'Usuário criado' : 'Senha redefinida'}
      footer={<Button onClick={onClose}>Entendi</Button>}
    >
      <p>Repasse estes dados para <strong>{secret.name}</strong>. A senha não será mostrada de novo.</p>
      <div className="au-secret">
        <div><span>Login</span><code>{secret.username}</code></div>
        <div><span>Senha temporária</span><code>{secret.password}</code></div>
        <Button size="sm" variant={copied ? 'accent' : 'outline'} icon={copied ? Check : Copy} onClick={copy}>
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <p className="au-form__hint">No primeiro acesso, a pessoa vai precisar criar uma senha própria.</p>
    </Modal>
  );
}

export default AdminUsers;
